<template>
  <v-dialog v-model="display" :width="dialogWidth">
    <template v-slot:activator="{ on }">
      <v-text-field
        v-bind="textFieldProps"
        :disabled="disabled"
        :loading="loading"
        :label="label"
        :value="formattedDatetime"
        v-on="on"
        readonly
      >
        <template v-slot:progress>
          <slot name="progress">
            <v-progress-linear color="primary" indeterminate absolute height="2"></v-progress-linear>
          </slot>
        </template>
      </v-text-field>
    </template>

    <v-card style="width: fit-content;">
      <vc-date-picker
        v-model="pendingDate"
        mode="dateTime"
        :is24hr="is24hr"
        :valid-hours="validHours"
        :min-date="minDate"
        :minute-increment="15"
        color="green"
      />
      <v-card-actions>
        <v-spacer></v-spacer>
        <slot name="actions" :parent="this">
          <v-btn color="grey lighten-1" text @click="clearHandler">{{ clearText }}</v-btn>
          <v-btn color="green darken-1" text @click="okHandler">{{ okText }}</v-btn>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { format } from 'date-fns'

const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd'
const DEFAULT_TIME_FORMAT = 'HH:mm:ss'
const DEFAULT_DIALOG_WIDTH = 300
const DEFAULT_CLEAR_TEXT = 'CLEAR'
const DEFAULT_OK_TEXT = 'OK'

export default {
  name: 'v-datetime-picker',
  model: {
    prop: 'datetime',
    event: 'input'
  },
  props: {
    datetime: {
      type: [Date, String],
      default: null
    },
    disabled: {
      type: Boolean
    },
    loading: {
      type: Boolean
    },
    label: {
      type: String,
      default: ''
    },
    dialogWidth: {
      type: Number,
      default: DEFAULT_DIALOG_WIDTH
    },
    dateFormat: {
      type: String,
      default: DEFAULT_DATE_FORMAT
    },
    timeFormat: {
      type: String,
      default: DEFAULT_TIME_FORMAT
    },
    clearText: {
      type: String,
      default: DEFAULT_CLEAR_TEXT
    },
    okText: {
      type: String,
      default: DEFAULT_OK_TEXT
    },
    textFieldProps: {
      type: Object
    },
    datePickerProps: {
      type: Object
    },
    timePickerProps: {
      type: Object
    }
  },
  data() {
    return {
      display: false,
      pendingDate: null
    }
  },
  mounted() {
    this.init()
  },
  computed: {
    dateTimeFormat() {
      return this.dateFormat + ' ' + this.timeFormat
    },
    formattedDatetime() {
      return this.pendingDate ? format(this.pendingDate, this.dateTimeFormat) : ''
    },
    is24hr() {
      return !/[hK]/.test(this.timeFormat)
    },
    minDate() {
      const allowedDates = this.datePickerProps && this.datePickerProps['allowed-dates']
      if (typeof allowedDates !== 'function') return null
      const today = new Date()
      for (let i = 0; i < 366; i++) {
        const candidate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i)
        if (allowedDates(format(candidate, DEFAULT_DATE_FORMAT))) {
          return candidate
        }
      }
      return null
    },
    validHours() {
      const allowedHours = this.timePickerProps && this.timePickerProps['allowed-hours']
      if (typeof allowedHours !== 'function') return null

      return (hour, dateParts) => {
        const date = new Date(dateParts.year, dateParts.month - 1, dateParts.day)
        return allowedHours(hour, format(date, DEFAULT_DATE_FORMAT))
      }
    }
  },
  methods: {
    init() {
      if (!this.datetime) {
        this.pendingDate = null
        return
      }

      this.pendingDate = this.datetime instanceof Date ? this.datetime : new Date(this.datetime)
    },
    okHandler() {
      this.display = false
      if (this.pendingDate && this.pendingDate < new Date()) {
        this.pendingDate = new Date()
      }
      this.$emit('input', this.pendingDate)
    },
    clearHandler() {
      this.display = false
      this.pendingDate = null
      this.$emit('input', null)
    }
  },
  watch: {
    datetime: function() {
      this.init()
    }
  }
}
</script>

<style>
::v-deep .vc-container {
    background-color: var(--secondary-background) !important;
    color: var(--secondary-text) !important;
    border: none !important;
  }

  ::v-deep .vc-title {
    color: var(--primary-text) !important;
  }

  ::v-deep .vc-weekday {
    color: var(--primary-text) !important;
  }

  ::v-deep .vc-day-content {
    color: var(--secondary-text);
  }

  ::v-deep .vc-day-content:hover {
    color: var(--primary-color) !important;
  }

  ::v-deep .vc-day-content:focus {
    background-color: var(--primary-color) !important;
  }

  ::v-deep .vc-arrow {
    color: var(--primary-color) !important;
  }

	#date-picker-menu::v-deep * {
		font-family: 'Poppins', sans-serif!important;
    left: calc(50% - 150px) !important;
	}

	.date-picker {
		width: 100%;
	}

	.date-column {
		border-left: 1px solid var(--primary-background);
	}

  ::v-deep .vc-highlight {
    background-color: var(--primary) !important;
  }

  ::v-deep .vc-day-content.is-disabled {
    color: var(--gray-400, #cbd5e0) !important;
  }
  </style>